import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    CircularProgress,
    Box,
    TextField,
    useMediaQuery,
    Pagination,
} from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PendingIcon from '@mui/icons-material/Pending';

const statusColor = {
    confirmed: "#98FF98",
    pending: "#FFD700",
    cancelled: "#FF6B6B",
    rescheduled: "#64B5F6",
    no_show: "#FF8A65",
};

const statusLabel = {
    confirmed: "Confirmed",
    pending: "Pending",
    cancelled: "Cancelled",
    rescheduled: "Rescheduled",
    no_show: "No Show",
};

const Appointments = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [allAppointments, setAllAppointments] = useState([]);
    const isMobile = useMediaQuery("(max-width: 850px)");
    const isMobileMd = useMediaQuery("(max-width: 720px)");
    const isMobileSm = useMediaQuery("(max-width: 500px)");
    const isMobileXs = useMediaQuery("(max-width: 390px)");
    const isMobileXXs = useMediaQuery("(max-width: 340px)");
    const apiLimit = 500;
    const pageLimit = 100;

    useEffect(() => {
        const fetchAppointments = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    page: 1,
                    limit: apiLimit,
                    searchQuery,
                });

                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/appointments?${params.toString()}`
                );

                if (!response.ok) {
                    throw new Error(`Failed to fetch appointments: ${response.statusText}`);
                }

                const data = await response.json();

                const sortedAppointments = [...data.data].sort((a, b) => {
                    const dateA = new Date(a["Requested Time"]);
                    const dateB = new Date(b["Requested Time"]);
                    return dateB - dateA;
                });

                setAllAppointments(sortedAppointments);
                setTotalCount(data.totalCount);
                setTotalPages(Math.ceil(sortedAppointments.length / pageLimit));
                setError(null);
            } catch (err) {
                console.error("Error fetching appointments:", err);
                setError(err.message);
                setAllAppointments([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAppointments();
    }, [searchQuery]);

    useEffect(() => {
        const startIndex = (page - 1) * pageLimit;
        const endIndex = startIndex + pageLimit;
        setAppointments(allAppointments.slice(startIndex, endIndex));
    }, [page, allAppointments]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        });
    };

    const formatRequestedTime = (timeString) => {
        if (!timeString) return "N/A";

        const parts = timeString.split(" ");
        if (parts.length >= 4) {
            return `${parts[0]} ${parts[1]}${isMobile ? "th, " : " ➜ "}  ${parts[3]} ${parts[4]}`;
        }
        return timeString;
    };

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="error" variant="h6">
                    Error: {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: isMobile ? 0 : 2 }}>
            <Box sx={{ mb: 3 }}>
                <TextField
                    label="Search Appointments"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={{
                        mb: 2,
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                borderColor: "#555",
                            },
                            "&:hover fieldset": {
                                borderColor: "#98FF98",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "#98FF98",
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: "#98FF98",
                        },
                        "& .MuiInputBase-input": {
                            color: "#fff",
                        },
                    }}
                />
                <Typography variant="body2" sx={{ color: "#BBB" }}>
                    Total Appointments: {totalCount}
                </Typography>
                <Typography variant="body2" sx={{ color: "#BBB" }}>
                    Showing: {(page - 1) * pageLimit + 1} - {Math.min(page * pageLimit, allAppointments.length)} of {allAppointments.length} appointments
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress sx={{ color: "#98FF98" }} />
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        background: "#282c34",
                        borderRadius: 3,
                        boxShadow: "0 4px 24px 0 rgba(0,0,0,0.25)",
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        textAlign: isMobile ? "center" : "left",
                                        color: "#ccc",
                                        fontSize: "1.1rem",
                                        background: "#151515",
                                        borderTopLeftRadius: "12px",
                                    }}
                                >
                                    <PersonIcon sx={{ mx: isMobileMd ? "0px" : "28px" }} />
                                </TableCell>
                                <TableCell
                                    sx={{

                                        textAlign: "center",
                                        color: "#ccc",
                                        fontSize: "1.1rem",
                                        background: "#151515",
                                    }}
                                >

                                    <AccessTimeIcon />
                                </TableCell>
                                <TableCell
                                    sx={{

                                        textAlign: "center",
                                        color: "#ccc",
                                        fontSize: "1.75rem",
                                        background: "#151515",
                                        display: isMobileMd ? "none" : "table-cell",
                                    }}
                                >
                                    <CalendarMonthIcon />
                                </TableCell>
                                <TableCell
                                    sx={{

                                        textAlign: "center",
                                        color: "#ccc",
                                        fontSize: "1.1rem",
                                        background: "#151515",
                                        display: isMobileXXs ? "none" : "table-cell",
                                    }}
                                >
                                    <SupportAgentIcon />
                                </TableCell>
                                <TableCell
                                    sx={{
                                        textAlign: "center",
                                        color: "#ccc",
                                        fontSize: "1.1rem",
                                        background: "#151515",
                                        borderTopRightRadius: "12px",
                                        display: isMobileMd ? "none" : "table-cell",
                                    }}
                                >
                                    <PendingIcon />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {appointments.map((appointment) => (
                                <TableRow
                                    key={appointment["Appointment Id"]}
                                    sx={{
                                        "&:nth-of-type(odd)": { background: "#181818" },
                                        "&:nth-of-type(even)": { background: "#212121" },
                                        "&:hover": { background: "#323232" },
                                    }}
                                >
                                    <TableCell sx={{
                                        fontWeight: "bold", textAlign: isMobile ? "center" : "left", width: isMobileXXs ? "50%" : isMobileSm ? "40%" : isMobileMd ? "30%" : isMobile ? "40%" : "20%", color: "#98FF98"
                                    }}>
                                        {appointment["Contact Name"] || "N/A"}
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: "center", width: isMobileXXs ? "50%" : isMobileXs ? "40% " : isMobileSm ? "30%" : isMobileMd ? "40%" : isMobile ? "20%" : "30%",
                                    }}>
                                        <span style={{ color: "#ccc", fontWeight: "bold" }}>{formatRequestedTime(appointment["Requested Time"])}</span> <span style={{ color: "#aaa", marginLeft: "4px", display: isMobile ? "none" : "" }}>ET</span>
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: "center", width: "20%", color: "#777", display: isMobileMd ? "none" : "table-cell"
                                    }}>
                                        {formatDate(appointment["Date Added"])}
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: "center", width: isMobileXs ? "20%" : isMobileSm ? "30%" : isMobileMd ? "30%" : isMobile ? "40%" : "20%", color: appointment.yiggy_user?.username ? "#84facd" : "#ccc", display: isMobileXXs ? "none" : "table-cell"
                                    }}>
                                        {appointment.yiggy_user?.username || "Self / AI"}
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: "center", width: "10%", display: isMobileMd ? "none" : "table-cell"
                                    }}>
                                        <Chip
                                            label={
                                                statusLabel[appointment.Outcome?.toLowerCase()] ||
                                                appointment.Outcome ||
                                                "Unknown"
                                            }
                                            sx={{
                                                background:
                                                    statusColor[appointment.Outcome?.toLowerCase()] ||
                                                    "#98FF98",
                                                color: "#22252b",
                                                fontWeight: "bold",
                                                borderRadius: "8px",
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {appointments.length === 0 && (
                        <Typography
                            sx={{
                                color: "#98FF98",
                                textAlign: "center",
                                py: 4,
                                fontWeight: "bold",
                            }}
                        >
                            No appointments found.
                        </Typography>
                    )}
                </TableContainer>
            )
            }

            {
                totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            variant="outlined"
                            shape="rounded"
                            sx={{
                                "& .MuiPaginationItem-root": {
                                    color: "#fff",
                                },
                                "& .MuiPaginationItem-page.Mui-selected": {
                                    backgroundColor: "#98FF98",
                                    color: "#282c34",
                                },
                            }}
                        />
                    </Box>
                )
            }
        </Box >
    );
};

export default Appointments;
